import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CardActions,
  Chip, 
  CircularProgress, 
  Divider, 
  Grid,
  IconButton, 
  Link,
  Menu, 
  MenuItem, 
  Paper, 
  Stack, 
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { KnowledgeManager } from '../../services/KnowledgeManager';
import { 
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Warning as WarningIcon,
  Security as SecurityIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format, subDays } from 'date-fns';

// Extract publication date from content snippet
const extractPublicationDate = (snippet) => {
  if (!snippet) return null;
  
  console.log('Extracting date from snippet:', snippet);
  
  // Check for "X days/weeks/months ago" patterns
  const timeAgoMatch = snippet.match(/^(\d+)\s+(day|days|week|weeks|month|months)\s+ago/i);
  if (timeAgoMatch) {
    const amount = parseInt(timeAgoMatch[1], 10);
    const unit = timeAgoMatch[2].toLowerCase();
    const today = new Date();
    
    if (unit === 'day' || unit === 'days') {
      console.log(`Found '${amount} days ago' pattern, calculating date`);
      return subDays(today, amount);
    } else if (unit === 'week' || unit === 'weeks') {
      console.log(`Found '${amount} weeks ago' pattern, calculating date`);
      return subDays(today, amount * 7);
    } else if (unit === 'month' || unit === 'months') {
      console.log(`Found '${amount} months ago' pattern, calculating date`);
      // Approximate a month as 30 days
      return subDays(today, amount * 30);
    }
  }
  
  // Check for "yesterday" or "today"
  if (snippet.match(/^yesterday/i)) {
    console.log('Found "yesterday" pattern');
    return subDays(new Date(), 1);
  }
  if (snippet.match(/^today/i)) {
    console.log('Found "today" pattern');
    return new Date();
  }
  
  // Check for month names at start (May 23, 2024 or May 23)
  const monthNameMatch = snippet.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2})(?:,?\s+(\d{4}))?/i);
  if (monthNameMatch) {
    console.log('Found month name pattern:', monthNameMatch[0]);
    const month = monthNameMatch[1];
    const day = parseInt(monthNameMatch[2], 10);
    const year = monthNameMatch[3] ? parseInt(monthNameMatch[3], 10) : new Date().getFullYear();
    
    // Create date using month name index
    const monthIndex = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
      .findIndex(m => month.toLowerCase().startsWith(m));
    
    if (monthIndex !== -1) {
      return new Date(year, monthIndex, day);
    }
  }
  
  // Check for standard date formats (MM/DD/YYYY or DD/MM/YYYY)
  const dateMatch = snippet.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
  if (dateMatch) {
    console.log('Found numeric date pattern:', dateMatch[0]);
    try {
      // Assume MM/DD/YYYY format for US-style dates
      const month = parseInt(dateMatch[1], 10) - 1; // 0-based month
      const day = parseInt(dateMatch[2], 10);
      const year = parseInt(dateMatch[3], 10);
      
      // Handle 2-digit years
      const fullYear = year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
      
      return new Date(fullYear, month, day);
    } catch (e) {
      console.error('Error parsing date:', e);
      return null;
    }
  }
  
  console.log('No date pattern found in snippet');
  return null;
};

// Format date for display
const formatDate = (dateString) => {
  try {
    return format(new Date(dateString), 'MMM d, yyyy');
  } catch (e) {
    return dateString;
  }
};

const CardResultsView = ({ creatorName, onExport, scanResults }) => {
  // Define getRiskLevel function at the top of the component to avoid reference errors
  const getRiskLevel = (match) => {
    const confidence = match.confidence || 0;
    const contentType = match.contentType || 'unknown';
    const domain = match.domain || '';
    
    // High-risk domains
    const highRiskDomains = ['onlyfans.com', 'patreon.com', 'fancentro.com'];
    const mediumRiskDomains = ['reddit.com', 'imgur.com', 'discord.com'];
    
    let riskScore = 0.5; // Base score
    
    // Domain-based risk
    if (highRiskDomains.some(d => domain.includes(d))) {
      riskScore += 0.3;
    } else if (mediumRiskDomains.some(d => domain.includes(d))) {
      riskScore += 0.2;
    }
    
    // Content type risk
    if (contentType === 'video') {
      riskScore += 0.2;
    } else if (contentType === 'image') {
      riskScore += 0.1;
    }
    
    // Confidence risk
    if (confidence >= 75) {
      riskScore += 0.2;
    }
    
    // Cap at 1.0
    riskScore = Math.min(1.0, riskScore);
    
    // Convert to percentage
    const riskPercentage = Math.round(riskScore * 100);
    
    // Determine the risk level based on percentage
    const label = riskPercentage >= 70 ? 'High' : riskPercentage >= 40 ? 'Medium' : 'Low';
    const level = label.toLowerCase(); // Add level property for filtering
    
    return {
      score: riskPercentage,
      label: label,
      level: level, // This is used for filtering
      color: riskPercentage >= 70 ? 'error' : riskPercentage >= 40 ? 'warning' : 'success'
    };
  };
  
  const [results] = useState(scanResults || {
    id: '',
    status: 'completed',
    creatorName: creatorName || 'Example Creator',
    timeframe: 'last 30 days', // Add default timeframe
    results: {
      totalMatches: 0,
      domains: [],
      matches: []
    }
  });
  
  // For debugging
  console.log('Scan Results:', results);
  const [filterContentType, setFilterContentType] = useState('all');
  const [filterDomain, setFilterDomain] = useState('all');
  const [filterConfidence, setFilterConfidence] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [resultsPerPage, setResultsPerPage] = useState(25); // Number of results to display per page
  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
  const [selectedResult, setSelectedResult] = useState(null);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  
  // Export functions
  // Function to copy all links to clipboard
  const copyAllLinks = () => {
    if (!results || !results.results || !results.results.matches || results.results.matches.length === 0) {
      console.log('No links to copy');
      return;
    }
    
    try {
      // Extract all URLs from the filtered results
      const allLinks = filteredResults
        .filter(match => match.url) // Only include items with URLs
        .map(match => match.url)
        .join('\n'); // Join with newlines for better readability
      
      // Copy to clipboard
      navigator.clipboard.writeText(allLinks)
        .then(() => {
          // Show success message (you could add a snackbar/toast here)
          console.log(`Copied ${filteredResults.length} links to clipboard`);
          alert(`Successfully copied ${filteredResults.length} links to clipboard`);
        })
        .catch(err => {
          console.error('Failed to copy links:', err);
          alert('Failed to copy links to clipboard');
        });
    } catch (error) {
      console.error('Error copying links:', error);
    }
  };
  
  const exportToCSV = () => {
    if (!results || !results.results || !results.results.matches || results.results.matches.length === 0) {
      console.log('No data to export');
      return;
    }
    
    try {
      // Create a new instance of KnowledgeManager
      const knowledgeManager = new KnowledgeManager();
      
      // First ensure the data is loaded into the KnowledgeManager
      if (results.results.matches && results.results.matches.length > 0) {
        // Add the data to the knowledge manager using the updateMasterContent method
        knowledgeManager.updateMasterContent(results.results.matches, results.creatorName);
      }
      
      // Get CSV data from KnowledgeManager
      const csvData = knowledgeManager.exportMasterData(results.creatorName, 'csv');
      if (!csvData) {
        console.error('Failed to generate CSV data');
        return;
      }
      
      // Create a blob and download link
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${results.creatorName.replace(/\s+/g, '_')}_content_leak_data.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
    }
  };
  
  const exportToPDF = () => {
    if (!results || !results.results || !results.results.matches || results.results.matches.length === 0) {
      console.log('No data to export');
      return;
    }
    
    try {
      // Get the filtered results
      const dataToExport = filteredResults;
      
      // Create a new window for the PDF
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow popups to export PDF');
        return;
      }
      
      // Create the HTML content for the PDF
      let htmlContent = `
        <html>
        <head>
          <title>Content Leak Report - ${results.creatorName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; }
            .header { margin-bottom: 20px; }
            .item { border: 1px solid #ddd; padding: 15px; margin-bottom: 15px; border-radius: 5px; }
            .item-header { display: flex; justify-content: space-between; margin-bottom: 10px; }
            .risk-high { color: #d32f2f; font-weight: bold; }
            .risk-medium { color: #f57c00; font-weight: bold; }
            .risk-low { color: #388e3c; font-weight: bold; }
            .url { color: #1976d2; word-break: break-all; }
            .content-type { display: inline-block; padding: 3px 8px; border-radius: 12px; font-size: 12px; }
            .type-video { background-color: #FF6B6B; color: white; }
            .type-image { background-color: #4ECDC4; color: white; }
            .type-text { background-color: #FFE66D; color: black; }
            .type-social { background-color: #F7FFF7; color: black; }
            .type-website { background-color: #3498DB; color: white; }
            .type-document { background-color: #9B59B6; color: white; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Content Leak Report</h1>
            <p><strong>Creator:</strong> ${results.creatorName}</p>
            <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
            <p><strong>Total Results:</strong> ${dataToExport.length}</p>
          </div>
      `;
      
      // Add each result item
      dataToExport.forEach((match, index) => {
        const contentTypeInfo = getContentTypeInfo(match.contentType, match);
        const riskLevel = getRiskLevel(match);
        
        htmlContent += `
          <div class="item">
            <div class="item-header">
              <span class="content-type type-${match.contentType || 'unknown'}">${contentTypeInfo.label}</span>
              <span class="risk-${riskLevel.level}">${riskLevel.label} Risk</span>
            </div>
            <h3>${match.title || 'No Title'}</h3>
            <p class="url">${match.url || ''}</p>
            <p>${match.contentSnippet || match.snippet || 'No description available'}</p>
          </div>
        `;
      });
      
      // Add footer
      htmlContent += `
          <div class="footer">
            <p>Generated by Content Leak Detection Tool</p>
          </div>
        </body>
        </html>
      `;
      
      // Write the HTML to the new window and print it
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for resources to load then print
      setTimeout(() => {
        printWindow.print();
      }, 500);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
    }
  };

  // Open URL in new tab
  const openInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  
  // Copy URL to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // In a real app, you'd show a toast notification here
    alert('URL copied to clipboard');
  };
  
  // Filter results based on search term and filters
  const filteredResults = useMemo(() => {
    if (!results || !results.results || !results.results.matches) return [];
    
    return results.results.matches.filter(match => {
      // Filter by search term
      const matchesSearch = searchTerm === '' || 
        (match.title && match.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (match.contentSnippet && match.contentSnippet.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (match.url && match.url.toLowerCase().includes(searchTerm.toLowerCase()));
        
      // Filter by domain
      const matchesDomain = filterDomain === 'all' || match.domain === filterDomain;
      
      // Filter by risk level using the level property from getRiskLevel
      const riskLevel = getRiskLevel(match);
      const matchesConfidence = filterConfidence === 'all' || filterConfidence === riskLevel.level;
        
      return matchesSearch && matchesDomain && matchesConfidence;
    });
  }, [results, filterDomain, filterConfidence, searchTerm]);
  
  // Pagination logic
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleResultsPerPageChange = (event) => {
    setResultsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to first page when changing results per page
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredResults.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const paginatedResults = filteredResults.slice(startIndex, endIndex);
  
  // Handle action menu open
  const handleActionClick = (event, result) => {
    setSelectedResult({ ...result, type: 'action' });
    setActionAnchorEl(event.currentTarget);
  };
  
  // Handle menu open
  const handleMenuOpen = (event, result) => {
    setSelectedResult({ ...result, type: 'menu' });
    setActionAnchorEl(event.currentTarget);
  };
  
  // Handle action menu close
  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedResult(null);
  };
  
  // Handle export action
  const handleExport = (format) => {
    if (onExport) {
      onExport(format);
    }
    handleActionClose();
  };
  
  // Get content type info for display
  const getContentTypeInfo = (contentType) => {
    switch(contentType) {
      case 'video':
        return { color: '#FF6B6B', label: 'Video' };
      case 'image':
        return { color: '#4ECDC4', label: 'Image' };
      case 'text':
        return { color: '#FFE66D', label: 'Text' };
      case 'social':
        return { color: '#F7FFF7', label: 'Social' };
      case 'website':
        return { color: '#3498DB', label: 'Website' };
      case 'document':
        return { color: '#9B59B6', label: 'Document' };
      default:
        // If URL is available, try to detect content type from URL
        if (contentType === 'unknown' && arguments[1] && arguments[1].url) {
          const url = arguments[1].url;
          // Check for video domains
          if (url.includes('youtube.com') || url.includes('vimeo.com') || url.includes('tiktok.com') || 
              url.includes('twitch.tv') || url.includes('/video/') || url.includes('watch?v=')) {
            return { color: '#FF6B6B', label: 'Video' };
          }
          // Check for image domains or extensions
          else if (url.includes('instagram.com') || url.includes('imgur.com') || url.includes('flickr.com') || 
                   url.includes('pinterest.com') || url.match(/\.(jpg|jpeg|png|gif|webp)($|\?)/i)) {
            return { color: '#4ECDC4', label: 'Image' };
          }
          // Check for social media
          else if (url.includes('facebook.com') || url.includes('twitter.com') || 
                   url.includes('linkedin.com') || url.includes('reddit.com')) {
            return { color: '#F7FFF7', label: 'Social' };
          }
          // Default to website
          else {
            return { color: '#3498DB', label: 'Website' };
          }
        }
        return { color: '#7F8C8D', label: 'Unknown' };
    }
  };
  
  // Risk level calculation is now defined at the top of the component
  
  return (
    <Box sx={{ width: '100%' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Scan Results for {creatorName || results.creatorName}
          </Typography>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />}
            onClick={(e) => handleActionClick(e, { type: 'export' })}
          >
            Export
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
          <Card variant="outlined" sx={{ flexGrow: 1, minWidth: '200px', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Total Results</Typography>
              <Typography variant="h3">{results.results.totalMatches}</Typography>
            </CardContent>
          </Card>
          
          <Card variant="outlined" sx={{ flexGrow: 1, minWidth: '200px', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Domains</Typography>
              <Typography variant="h3">{results.results.domains.length}</Typography>
            </CardContent>
          </Card>
          
          <Card variant="outlined" sx={{ flexGrow: 1, minWidth: '200px', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Date Range</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                {(() => {
                  // Current date for end date
                  const endDate = new Date();
                  
                  // Calculate start date based on timeframe
                  let startDate = new Date();
                  let daysToSubtract = 30; // Default 30 days
                  
                  // Debug: Log scan results and timeframe
                  console.log('Scan Results:', scanResults);
                  console.log('Original Props:', { creatorName, onExport, scanResults });
                  
                  // Get timeframe from scan results
                  if (scanResults && scanResults.timeframe) {
                    const timeframe = scanResults.timeframe.toLowerCase();
                    console.log('Timeframe found:', timeframe);
                    
                    if (timeframe === 'today') {
                      daysToSubtract = 0;
                    } else {
                      // Parse "last X days/weeks/months" format
                      const match = timeframe.match(/last\s+(\d+)\s+(day|days|week|weeks|month|months)/i);
                      if (match) {
                        const amount = parseInt(match[1], 10);
                        const unit = match[2].toLowerCase();
                        console.log('Parsed timeframe:', { amount, unit });
                        
                        // Calculate days based on unit
                        if (unit === 'day' || unit === 'days') {
                          daysToSubtract = amount;
                        } else if (unit === 'week' || unit === 'weeks') {
                          daysToSubtract = amount * 7; // Convert weeks to days
                        } else if (unit === 'month' || unit === 'months') {
                          daysToSubtract = amount * 30; // Approximate months as 30 days
                        }
                      } else {
                        console.log('Could not parse timeframe pattern:', timeframe);
                      }
                    }
                  } else {
                    console.log('No timeframe found in scan results, using default 30 days');
                  }
                  
                  console.log('Days to subtract:', daysToSubtract);
                  
                  // Calculate start date by subtracting days
                  startDate.setDate(endDate.getDate() - daysToSubtract);
                  
                  console.log('Start Date:', startDate.toISOString());
                  console.log('End Date:', endDate.toISOString());
                  
                  return (
                    <>
                      <Typography variant="body2">
                        {formatDate(startDate.toISOString())}
                      </Typography>
                      <Typography variant="body2">to</Typography>
                      <Typography variant="body2">
                        {formatDate(endDate.toISOString())}
                      </Typography>
                    </>
                  );
                })()} 
              </Box>
            </CardContent>
          </Card>
        </Box>
        
        <Paper variant="outlined" sx={{ mb: 4, p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <TextField
              placeholder="Search results..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )
              }}
              sx={{ flexGrow: 1, minWidth: '200px' }}
            />
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FilterListIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">Domain:</Typography>
              <Box component="select" 
                value={filterDomain}
                onChange={(e) => setFilterDomain(e.target.value)}
                sx={{ 
                  p: 1, 
                  borderRadius: 1, 
                  border: '1px solid #e0e0e0',
                  minWidth: '100px'
                }}
              >
                <option value="all">All Domains</option>
                {results.results.domains.map(domain => (
                  <option key={domain} value={domain}>{domain}</option>
                ))}
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">Risk Level:</Typography>
              <Box component="select" 
                value={filterConfidence}
                onChange={(e) => setFilterConfidence(e.target.value)}
                sx={{ 
                  p: 1, 
                  borderRadius: 1, 
                  border: '1px solid #e0e0e0',
                  minWidth: '100px'
                }}
              >
                <option value="all">All Levels</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </Box>
            </Box>
            
            {/* Export Buttons */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
              <Tooltip title="Export to CSV">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CsvIcon />}
                  onClick={exportToCSV}
                  sx={{ minWidth: 'auto' }}
                >
                  CSV
                </Button>
              </Tooltip>
              <Tooltip title="Export to PDF">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PdfIcon />}
                  onClick={exportToPDF}
                  sx={{ minWidth: 'auto' }}
                >
                  PDF
                </Button>
              </Tooltip>
              <Tooltip title="Copy All Links">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={copyAllLinks}
                  sx={{ minWidth: 'auto' }}
                >
                  Copy Links
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'flex-start' }}>
          {paginatedResults.map((match) => {
            // Only use content type and risk level, removing confidence level
            const contentTypeInfo = getContentTypeInfo(match.contentType, match);
            const riskLevel = getRiskLevel(match);
            
            return (
              <Box key={match.id || match.url} sx={{ width: '100%', mb: 3 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  style={{ height: '100%' }}
                >
                  <Card variant="outlined" sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    width: '100%',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    '&:hover': {
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    }
                  }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={contentTypeInfo.label} 
                            size="small" 
                            sx={{ 
                              backgroundColor: contentTypeInfo.color,
                              color: contentTypeInfo.color === '#F7FFF7' || contentTypeInfo.color === '#FFE66D' ? '#000' : '#fff'
                            }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                              {(() => {
                                // First try to extract date from the snippet
                                const snippet = match.contentSnippet || match.snippet;
                                const extractedDate = extractPublicationDate(snippet);
                                
                                if (extractedDate) {
                                  // Add a small indicator to show this is a publication date
                                  return (
                                    <>
                                      <span style={{ color: '#4caf50', marginRight: '4px' }}>📅</span>
                                      {format(extractedDate, 'MMM d, yyyy')}
                                    </>
                                  );
                                }
                                
                                // If no date in snippet, try other fields
                                if (match.date) {
                                  return (
                                    <>
                                      <span style={{ color: '#ff9800', marginRight: '4px' }}>📅</span>
                                      {formatDate(match.date)}
                                    </>
                                  );
                                }
                                
                                // Fallback to "Unknown date"
                                return (
                                  <>
                                    <span style={{ color: '#f44336', marginRight: '4px' }}>❓</span>
                                    Unknown date
                                  </>
                                );
                              })()}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Chip 
                            label={`Risk: ${riskLevel.label}`}
                            size="small"
                            color={riskLevel.color}
                          />
                        </Box>
                      </Box>
                      
                      <Typography variant="h6" gutterBottom noWrap title={match.title || ''}>
                        {match.title || 'Untitled Content'}
                      </Typography>
                      
                      <Link 
                        href={match.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        underline="hover"
                        color="primary"
                        sx={{ 
                          display: 'block', 
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {match.url || ''}
                      </Link>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {(match.contentSnippet || match.snippet || 'No description available')
                          .replace(/^(\d+)\s+days?\s+ago\s+\.\.\.\s+/, '')
                          .replace(/^(\d{1,2}\s+\w+\s+\d{4}|\w+\s+\d{1,2},\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{2,4})\s+\.\.\.\s+/, '')}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                        <Box sx={{ ml: 'auto' }}>
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleMenuOpen(e, match)}
                            aria-label="More options"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                    
                    <Divider />
                    
                    <CardActions>
                      <Button 
                        size="small" 
                        startIcon={<OpenInNewIcon />}
                        onClick={() => openInNewTab(match.url)}
                      >
                        Open
                      </Button>
                      <Button 
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => copyToClipboard(match.url)}
                      >
                        Copy URL
                      </Button>
                      <Box sx={{ flexGrow: 1 }} />
                      <IconButton size="small" onClick={(e) => handleActionClick(e, match)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </motion.div>
              </Box>
            );
          })}
        </Box>
        
        {filteredResults.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="h6" color="text.secondary">No results found</Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filters
            </Typography>
          </Box>
        )}
        
        {/* Pagination Controls */}
        {filteredResults.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredResults.length)} of {filteredResults.length} results
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Results per page:</Typography>
                <Box component="select" 
                  value={resultsPerPage}
                  onChange={handleResultsPerPageChange}
                  sx={{ 
                    p: 1, 
                    borderRadius: 1, 
                    border: '1px solid #e0e0e0',
                    minWidth: '70px'
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  size="small" 
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </Button>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Show pages around current page
                    let pageToShow;
                    if (totalPages <= 5) {
                      // If 5 or fewer pages, show all
                      pageToShow = i + 1;
                    } else if (currentPage <= 3) {
                      // If near start, show first 5 pages
                      pageToShow = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // If near end, show last 5 pages
                      pageToShow = totalPages - 4 + i;
                    } else {
                      // Otherwise show 2 before and 2 after current page
                      pageToShow = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button 
                        key={pageToShow}
                        variant={currentPage === pageToShow ? "contained" : "outlined"}
                        size="small"
                        onClick={() => handlePageChange(pageToShow)}
                        sx={{ minWidth: '40px' }}
                      >
                        {pageToShow}
                      </Button>
                    );
                  })}
                </Box>
                
                <Button 
                  variant="outlined" 
                  size="small" 
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
      </motion.div>
      
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionClose}
      >
        {selectedResult && selectedResult.type === 'export' ? (
          <>
            <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
            <MenuItem onClick={() => handleExport('json')}>Export as JSON</MenuItem>
            <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
            <MenuItem onClick={() => handleExport('pdf')}>Export as PDF Report</MenuItem>
          </>
        ) : selectedResult && selectedResult.type === 'menu' ? (
          <>
            <MenuItem onClick={() => openInNewTab(selectedResult.url)}>
              <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
              Open in new tab
            </MenuItem>
            <MenuItem onClick={() => copyToClipboard(selectedResult.url)}>
              <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
              Copy URL
            </MenuItem>
            <Divider />
            <MenuItem>
              <FlagIcon fontSize="small" sx={{ mr: 1 }} />
              Report as false positive
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={() => handleActionClose()}>
              <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
              View details
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default CardResultsView;
